// src/pages/TermsOfService.tsx
import React, { useState, useEffect } from "react";
import { PageHeader } from "../components/PageHeader.tsx";
import "./TermsOfService.css";

export const TermsOfService: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("");

  const sections = [
    { id: "acceptance", title: "1. Acceptance of Terms" },
    { id: "eligibility", title: "2. Eligibility" },
    { id: "account", title: "3. Account Registration" },
    { id: "service", title: "4. Use of the Service" },
    { id: "conduct", title: "5. User Conduct" },
    { id: "chat", title: "6. Chat and Communication" },
    { id: "content", title: "7. User Content" },
    { id: "userdata", title: "8. User Data" },
    { id: "intellectual", title: "9. Intellectual Property" },
    { id: "advertising", title: "10. Third-Party Advertising" },
    { id: "geographic", title: "11. Geographic Restrictions" },
    { id: "termination", title: "12. Termination" },
    { id: "disclaimers", title: "13. Disclaimers" },
    { id: "liability", title: "14. Limitation of Liability" },
    { id: "indemnification", title: "15. Indemnification" },
    { id: "disputes", title: "16. Dispute Resolution" },
    { id: "changes", title: "17. Changes to Terms" },
    { id: "electronic", title: "18. Electronic Communications" },
    { id: "general", title: "19. General Provisions" },
    { id: "contact", title: "20. Contact Information" }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id)
      }));

      for (const section of sectionElements) {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="terms-page">
      <PageHeader
        icon="📜"
        title="Terms of Service"
        description="Rules and guidelines for using Expanded Chess"
      />

      <div className="terms-content">
        {/* Table of Contents - Sidebar */}
        <nav className="terms-nav">
          <div className="nav-sticky">
            <h3 className="nav-title">Contents</h3>
            <ul className="nav-list">
              {sections.map(section => (
                <li key={section.id}>
                  <button
                    className={`nav-link ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Main Content */}
        <main className="terms-main">
          <div className="terms-card">
            {/* Print Button - Top */}
            <div className="terms-header-actions">
              <button
                className="print-btn"
                onClick={() => window.print()}
              >
                🖨️ Print / Save as PDF
              </button>
            </div>

            {/* Last Updated Banner */}
            <div className="last-updated">
              <span className="update-icon">📅</span>
              <span><strong>Effective Date:</strong> January 7, 2025</span>
            </div>

            {/* Introduction */}
            <div className="intro-box">
              <p>
                Welcome to Expanded Chess. These Terms of Service ("Terms") constitute a legally
                binding agreement between you ("User," "you," or "your") and Expanded Chess
                ("Company," "we," "us," or "our") governing your access to and use of the
                Expanded Chess platform, website, and related services (collectively, the "Service").
              </p>
              <p>
                By accessing, browsing, or using the Service, you acknowledge that you have read,
                understood, and agree to be bound by these Terms and our <a href="/privacy">Privacy Policy</a>.
                If you do not agree to these Terms, you shall not access or use the Service.
              </p>
              <p>
                We may modify these Terms at any time. When we make material changes, we will update
                the "Effective Date" at the top of these Terms and, where appropriate, provide notice
                (for example, by email or via a notice on the Service). Your continued use of the
                Service following any modification constitutes your acceptance of the modified Terms.
              </p>
            </div>

            {/* Section 1: Acceptance of Terms */}
            <section id="acceptance" className="terms-section">
              <h2>1. Acceptance of Terms</h2>
              <p>By accessing or using the Service, you agree to be bound by:</p>
              <ul>
                <li>These Terms; and</li>
                <li>Our Privacy Policy, which is incorporated into these Terms by reference.</li>
              </ul>
              <p>
                If you do not agree to these Terms or the Privacy Policy, you must not use the Service.
              </p>
            </section>

            {/* Section 2: Eligibility */}
            <section id="eligibility" className="terms-section">
              <h2>2. Eligibility</h2>

              <h3>2.1 Age Requirement</h3>
              <div className="important-box">
                <strong>⚠️ Age Requirement</strong>
                <p>
                  You shall be at least <strong>18 years of age</strong> to use our Service. By registering for
                  an account or using our Service, you represent and warrant that you are at
                  least 18 years old.
                </p>
              </div>
              <p>
                We do not knowingly collect personal information from children under the age of 18.
                If you are a parent or guardian and believe that a child under 18 has provided us
                with personal information, please contact us at info@expandedchess.com. We will take steps
                to delete such information.
              </p>

              <h3>2.2 Additional Eligibility Requirements</h3>
              <p>To use our Service, you shall:</p>
              <ul>
                <li>Be at least 18 years of age;</li>
                <li>Have the legal capacity to enter into a binding agreement;</li>
                <li>Not be located in a jurisdiction where access to the Service is prohibited; and</li>
                <li>Not have been previously banned or suspended from the Service.</li>
              </ul>

              <h3>2.3 Accurate Age Information</h3>
              <p>
                You shall provide accurate and truthful date of birth information during the
                registration process to verify your eligibility. Providing false age information
                is a material breach of these Terms and grounds for immediate account termination.
              </p>
            </section>

            {/* Section 3: Account Registration */}
            <section id="account" className="terms-section">
              <h2>3. Account Registration</h2>

              <h3>3.1 Account Creation</h3>
              <p>To access certain features of the Service, you may be required to create an account by providing:</p>
              <ul>
                <li>A unique username;</li>
                <li>A valid email address;</li>
                <li>A secure password; and</li>
                <li>Date of birth (submitted for age-verification purposes only).</li>
              </ul>

              <h3>3.2 Account Responsibilities</h3>
              <p>You shall:</p>
              <ul>
                <li>Provide accurate, current, and complete information during registration;</li>
                <li>Maintain and promptly update your account information to keep it accurate;</li>
                <li>Maintain the security and confidentiality of your login credentials;</li>
                <li>Not share your account credentials with any third party;</li>
                <li>Immediately notify us of any unauthorized use of your account or other breach of security; and</li>
                <li>Accept responsibility for all activities that occur under your account.</li>
              </ul>

              <h3>3.3 Account Security</h3>
              <p>
                You are solely responsible for maintaining the confidentiality of your account
                credentials. You shall not transfer, sell, or otherwise share your account with
                any other person. We may suspend or terminate accounts that we reasonably believe
                have been compromised or are being misused.
              </p>

              <h3>3.4 One Account Per User</h3>
              <p>
                Each user shall maintain only one account. Creating multiple accounts to circumvent
                bans, manipulate rankings, or for any other improper purpose is strictly prohibited.
              </p>
            </section>

            {/* Section 4: Use of the Service */}
            <section id="service" className="terms-section">
              <h2>4. Use of the Service</h2>

              <h3>4.1 License Grant</h3>
              <p>
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
                non-transferable, revocable license to access and use the Service for your <strong>personal,
                non-commercial</strong> use.
              </p>

              <h3>4.2 Service Description</h3>
              <p>
                Expanded Chess is a chess variant gaming platform that provides users with the
                ability to:
              </p>
              <ul>
                <li>Play chess variants featuring custom pieces;</li>
                <li>Use multiple board sizes;</li>
                <li>Play single-player games against AI opponents;</li>
                <li>Play multiplayer games against other users; and</li>
                <li>Access related features, such as game history, rankings, and chat.</li>
              </ul>

              <h3>4.3 Service Availability</h3>
              <p>
                We strive to maintain Service availability but do not guarantee uninterrupted,
                timely, secure, or error-free operation. You acknowledge that:
              </p>
              <ul>
                <li>The Service may be unavailable due to maintenance, updates, or technical issues; and</li>
                <li>We may modify, suspend, or discontinue all or part of the Service at any time, with or without notice, and we have no obligation to continue providing the Service.</li>
              </ul>

              <h3>4.4 Prohibited Uses</h3>
              <p>You shall not:</p>
              <ul>
                <li>Use the Service for any illegal purpose or in violation of any applicable law or regulation;</li>
                <li>Attempt to gain unauthorized access to any part of the Service or any related system or network;</li>
                <li>Use any automated means (including bots, scripts, or scrapers) to access or interact with the Service without our express written permission;</li>
                <li>Interfere with or disrupt the integrity, performance, or security of the Service;</li>
                <li>Probe, scan, or test the vulnerability of the Service or any related network;</li>
                <li>Circumvent or attempt to circumvent any security or access-control measures;</li>
                <li>Use the Service to transmit malware, viruses, or other malicious code;</li>
                <li>Reverse engineer, decompile, or disassemble any portion of the Service, except to the limited extent expressly permitted by applicable law; or</li>
                <li>Use the Service to collect personal information about other users without their consent.</li>
              </ul>

              <h3>4.5 Services Management</h3>
              <p>
                We reserve the right, but not the obligation, to:
              </p>
              <ul>
                <li>
                  Monitor the Service for violations of these Terms, including reviewing
                  gameplay patterns, chat logs, and user reports;
                </li>
                <li>
                  Take appropriate legal action against anyone who, in our sole discretion,
                  violates the law or these Terms, including reporting such user to law
                  enforcement authorities;
                </li>
                <li>
                  In our sole discretion and without limitation, refuse, restrict access to,
                  limit the availability of, or disable any of your User Content, account
                  features, or any portion thereof;
                </li>
                <li>
                  Remove from the Service or otherwise disable all files, content, or data
                  that are excessive in size or are in any way burdensome to our systems;
                </li>
                <li>
                  Implement automated systems to detect cheating, exploitation, or other
                  prohibited activities; and
                </li>
                <li>
                  Otherwise manage the Service in a manner designed to protect our rights
                  and property, ensure fair play, and facilitate the proper functioning
                  of the Service.
                </li>
              </ul>

              <h3>4.6 Service Modifications</h3>
              <p>
                We reserve the right to modify, suspend, or discontinue any aspect of the
                Service at any time, including:
              </p>
              <ul>
                <li>Game rules, piece behaviors, or board configurations;</li>
                <li>Ranking algorithms and matchmaking systems;</li>
                <li>Features, functionality, or user interface elements;</li>
                <li>Server locations or technical infrastructure; and</li>
                <li>Availability of specific game modes or variants.</li>
              </ul>
              <p>
                We will endeavor to provide reasonable notice of significant changes where
                practicable, but are under no obligation to do so.
              </p>
            </section>

            {/* Section 5: User Conduct */}
            <section id="conduct" className="terms-section">
              <h2>5. User Conduct</h2>

              <h3>5.1 General Conduct Standards</h3>
              <p>When using the Service, you shall:</p>
              <ul>
                <li>Treat other users with respect and courtesy;</li>
                <li>Follow fair play principles during games;</li>
                <li>Use the Service only for lawful purposes; and</li>
                <li>Comply with all applicable laws, regulations, and these Terms.</li>
              </ul>

              <h3>5.2 Prohibited Conduct</h3>
              <p>You shall not:</p>
              <ul>
                <li>Harass, bully, or intimidate other users;</li>
                <li>Use hate speech, slurs, or discriminatory language;</li>
                <li>Threaten or encourage violence or harm;</li>
                <li>Impersonate any person or entity;</li>
                <li>Cheat or exploit bugs or glitches to gain an unfair advantage;</li>
                <li>Use external tools or software to gain an unfair advantage;</li>
                <li>Intentionally disconnect, stall, or abandon games to avoid losses;</li>
                <li>Manipulate rankings, statistics, or matchmaking through collusion or other unfair means;</li>
                <li>Spam, advertise, or solicit other users without our prior consent; or</li>
                <li>Share illegal, malicious, or otherwise inappropriate content or links.</li>
              </ul>

              <h3>5.3 Enforcement</h3>
              <p>
                We may, in our sole discretion, investigate and take appropriate action against
                users who violate these standards. Such action may include, without limitation:
              </p>
              <ul>
                <li>Removing or modifying User Content;</li>
                <li>Issuing warnings;</li>
                <li>Temporarily suspending account access;</li>
                <li>Permanently banning accounts; and</li>
                <li>Reporting violations to law enforcement authorities where appropriate.</li>
              </ul>
            </section>

            {/* Section 6: Chat and Communication Features */}
            <section id="chat" className="terms-section">
              <h2>6. Chat and Communication Features</h2>

              <h3>6.1 Chat Functionality</h3>
              <p>
                The Service may offer chat or messaging features that allow users to communicate
                with each other during gameplay. By using chat, you agree to comply with all
                conduct standards in Section 5.
              </p>

              <h3>6.2 Chat Rules</h3>
              <p>When using chat features, you shall:</p>
              <ul>
                <li>Communicate respectfully;</li>
                <li>Not share personal information (your own or others'), such as physical addresses, phone numbers, or financial information;</li>
                <li>Not solicit personal information from others;</li>
                <li>Not send unsolicited commercial messages; and</li>
                <li>Not share malicious, illegal, or inappropriate links.</li>
              </ul>

              <h3>6.3 Prohibited Chat Content</h3>
              <p>You shall not use chat features to share or transmit:</p>
              <ul>
                <li>Sexual, pornographic, or obscene content;</li>
                <li>Content that exploits or endangers minors;</li>
                <li>Violent, threatening, or graphic content;</li>
                <li>Hate speech or content promoting discrimination;</li>
                <li>Content that promotes illegal activities;</li>
                <li>Spam, phishing attempts, or scam content; or</li>
                <li>Content that infringes third-party intellectual property rights.</li>
              </ul>

              <h3>6.4 Message Retention and Monitoring</h3>
              <p>
                Chat messages are retained for 90 days for safety, moderation,
                and troubleshooting. We may review chat messages in connection with reports of
                abuse, security issues, or other legitimate purposes. By using chat features,
                you acknowledge and consent to this limited retention and monitoring.
              </p>

              <h3>6.5 Reporting Violations</h3>
              <p>
                If you encounter inappropriate behavior or content, you should report it using
                the in-game reporting tools or contact us at info@expandedchess.com. We will review
                and take appropriate action.
              </p>
            </section>

            {/* Section 7: User Content */}
            <section id="content" className="terms-section">
              <h2>7. User Content</h2>

              <h3>7.1 User-Generated Content</h3>
              <p>
                The Service may allow you to create, submit, and share content, including but
                not limited to custom chess variants, game configurations, and chat messages
                ("User Content").
              </p>

              <h3>7.2 Ownership and License</h3>
              <p>
                You retain ownership of your original User Content. By submitting User Content
                to the Service, you grant Expanded Chess a worldwide, non-exclusive, royalty-free,
                transferable, and sublicensable license to use, reproduce, host, store, modify,
                adapt, translate, publish, publicly display, publicly perform, distribute, and
                create derivative works of your User Content in connection with operating,
                improving, and promoting the Service.
              </p>
              <p>
                This license continues for as long as we reasonably need the content for these
                purposes and may survive account termination to the extent necessary to:
              </p>
              <ul>
                <li>Maintain operation and security logs;</li>
                <li>Enforce our Terms; and</li>
                <li>Comply with legal obligations.</li>
              </ul>

              <h3>7.3 Representations and Warranties</h3>
              <p>By submitting User Content, you represent and warrant that:</p>
              <ul>
                <li>You own or have the necessary rights to use and authorize the use of your User Content;</li>
                <li>Your User Content does not infringe or violate any third party's intellectual property, privacy, or other rights; and</li>
                <li>Your User Content complies with these Terms and all applicable laws.</li>
              </ul>

              <h3>7.4 Content Removal</h3>
              <p>
                We may remove or disable access to any User Content that we determine, in our
                sole discretion, violates these Terms, infringes on intellectual property rights,
                or is otherwise objectionable. We are not obligated to store or back up any User Content.
              </p>
            </section>

            {/* Section 8: User Data */}
            <section id="userdata" className="terms-section">
              <h2>8. User Data</h2>

              <h3>8.1 Data You Provide</h3>
              <p>
                We will maintain certain data that you transmit to the Service for the
                purpose of managing the performance of the Service, as well as data
                relating to your use of the Service. This includes, but is not limited to:
              </p>
              <ul>
                <li>Account information and preferences;</li>
                <li>Game history, statistics, and rankings;</li>
                <li>Custom game configurations and variants you create;</li>
                <li>Chat messages and communications; and</li>
                <li>Any other content you submit through the Service.</li>
              </ul>

              <h3>8.2 Data Backups</h3>
              <p>
                Although we perform regular routine backups of data, you are solely
                responsible for all data that you transmit or that relates to any
                activity you have undertaken using the Service. We do not guarantee
                that backups will be available or that any specific data can be recovered.
              </p>

              <h3>8.3 No Liability for Data Loss</h3>
              <div className="legal-box">
                <p>
                  YOU AGREE THAT WE SHALL HAVE NO LIABILITY TO YOU FOR ANY LOSS,
                  CORRUPTION, OR UNAUTHORIZED ACCESS OF ANY DATA, INCLUDING BUT NOT
                  LIMITED TO GAME HISTORY, ACCOUNT INFORMATION, RANKINGS, OR USER
                  CONTENT. YOU HEREBY WAIVE ANY RIGHT OF ACTION AGAINST US ARISING
                  FROM ANY SUCH LOSS, CORRUPTION, OR UNAUTHORIZED ACCESS OF DATA.
                </p>
              </div>

              <h3>8.4 Data Portability</h3>
              <p>
                For information about accessing or exporting your personal data, please
                refer to our <a href="/privacy">Privacy Policy</a> or contact us at{" "}
                <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>.
              </p>
            </section>

            {/* Section 9: Intellectual Property */}
            <section id="intellectual" className="terms-section">
              <h2>9. Intellectual Property</h2>

              <h3>9.1 Our Intellectual Property</h3>
              <p>
                Except for User Content, all content, features, and functionality of the Service—including
                software, design, graphics, text, custom chess pieces, and related materials—are owned by
                Expanded Chess or our licensors and are protected by copyright, trademark, and other
                intellectual property laws.
              </p>

              <h3>9.2 Trademarks</h3>
              <p>
                "Expanded Chess," our logo, and any related names, logos, product and service names,
                designs, and slogans are trademarks of Expanded Chess. You shall not use such marks
                without our prior written consent.
              </p>

              <h3>9.3 Restrictions</h3>
              <p>You shall not:</p>
              <ul>
                <li>Copy, modify, or create derivative works of the Service or its content;</li>
                <li>Use any of our intellectual property for commercial purposes without authorization;</li>
                <li>Remove any copyright, trademark, or other proprietary notices; or</li>
                <li>Attempt to extract source code or underlying ideas from the Service.</li>
              </ul>

              <h3>9.4 Feedback</h3>
              <p>
                If you provide suggestions, ideas, or feedback regarding the Service ("Feedback"),
                you grant us the unrestricted right to use and implement such Feedback without
                compensation or attribution to you.
              </p>
            </section>

            {/* Section 10: Third-Party Advertising and Links */}
            <section id="advertising" className="terms-section">
              <h2>10. Third-Party Advertising and Links</h2>

              <h3>10.1 Advertising Content</h3>
              <p>
                The Service may display advertisements, sponsored content, or promotional messages
                ("Ads") provided by third-party ad networks or advertisers. Ads may appear in various
                formats, including banners, interstitials, or contextual placements within the Service.
              </p>

              <h3>10.2 No Endorsement</h3>
              <p>
                We do not endorse, guarantee, or recommend any product, service, or content advertised
                on or linked from the Service. Your interactions with advertisers, including payment
                and delivery of goods or services, are solely between you and the advertiser. We are
                not responsible for any loss or damage of any sort incurred as the result of such dealings.
              </p>

              <h3>10.3 Third-Party Terms</h3>
              <p>
                Third-party advertisers and ad networks may have their own terms of service and privacy
                policies. You are responsible for reviewing and complying with such third-party terms
                and policies. Your use of third-party sites or services is at your own risk.
              </p>

              <h3>10.4 Advertising Technologies</h3>
              <p>
                Third-party advertisers and ad networks may use cookies, pixels, device identifiers,
                or other tracking technologies to:
              </p>
              <ul>
                <li>Deliver more relevant ads;</li>
                <li>Measure ad performance and effectiveness;</li>
                <li>Limit how often you see a particular ad;</li>
                <li>Detect and prevent fraud and abuse.</li>
              </ul>
              <p>
                For more information about these technologies and your choices, please review our{" "}
                <a href="/privacy">Privacy Policy</a> and Cookie Policy.
              </p>
            </section>

            {/* Section 11: Geographic Restrictions and VPN Use */}
            <section id="geographic" className="terms-section">
              <h2>11. Geographic Restrictions and VPN Use</h2>

              <h3>11.1 Service Availability</h3>
              <p>
                Our Service may be unavailable or restricted in certain jurisdictions due to
                regulatory or other legal reasons. Currently restricted regions include, without limitation:
              </p>
              <ul>
                <li>China;</li>
                <li>South Korea;</li>
                <li>Russia;</li>
                <li>Vietnam.</li>
              </ul>
              <p>We may update this list at any time.</p>

              <h3>11.2 Prohibited Circumvention</h3>
              <p>
                You shall not use virtual private networks (VPNs), proxy servers, or similar
                technologies to circumvent geographic restrictions and access the Service from
                a restricted region.
              </p>

              <h3>11.3 Consequences of Circumvention</h3>
              <p>If you access the Service from a restricted region through any circumvention method:</p>
              <ul>
                <li>You are solely responsible for compliance with local laws;</li>
                <li>You acknowledge that we have taken reasonable measures to restrict access from such regions;</li>
                <li>Your account may be terminated without notice; and</li>
                <li>You release us from any liability arising from your access from the restricted region.</li>
              </ul>

              <h3>11.4 Geographic Blocking</h3>
              <p>
                We may use IP-based blocking or other means to restrict access from certain regions.
                Our efforts do not guarantee that all restricted jurisdictions are fully blocked.
              </p>
            </section>

            {/* Section 12: Termination */}
            <section id="termination" className="terms-section">
              <h2>12. Termination</h2>

              <h3>12.1 Termination by You</h3>
              <p>
                You may terminate your account at any time by contacting us at info@expandedchess.com
                or by using available account deletion features. Upon termination, your right to
                use the Service will immediately cease.
              </p>

              <h3>12.2 Termination by Us</h3>
              <p>We may suspend or terminate your account or access to the Service, with or without notice, if:</p>
              <ul>
                <li>You violate these Terms or other policies;</li>
                <li>We suspect fraudulent, abusive, or illegal activity;</li>
                <li>Your conduct harms or threatens the Service, other users, or third parties;</li>
                <li>We are required to do so by law; or</li>
                <li>We choose, in our sole discretion, to discontinue all or part of the Service.</li>
              </ul>

              <h3>12.3 Effect of Termination</h3>
              <p>Upon termination:</p>
              <ul>
                <li>All rights and licenses granted to you under these Terms immediately terminate;</li>
                <li>We may delete or anonymize your account information and User Content in accordance with our Privacy Policy; and</li>
                <li>Provisions that by their nature should survive termination (e.g., intellectual property, disclaimers, limitations of liability, indemnification, dispute resolution) shall survive.</li>
              </ul>

              <h3>12.4 No Refunds</h3>
              <p>
                The Service is currently free of charge. If paid features are offered in the future
                and your account is terminated due to a violation of these Terms, you will not be
                entitled to any refunds.
              </p>
            </section>

            {/* Section 13: Disclaimers */}
            <section id="disclaimers" className="terms-section">
              <h2>13. Disclaimers</h2>

              <div className="legal-box">
                <h3>13.1 "As Is" and "As Available"</h3>
                <p>
                  THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT
                  WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING
                  BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                  PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
                </p>

                <h3>13.2 No Guarantee</h3>
                <p>Without limiting the foregoing, we do not warrant that:</p>
                <ul>
                  <li>The Service will meet your requirements or expectations;</li>
                  <li>The Service will be uninterrupted, timely, secure, or error-free;</li>
                  <li>Any defects or errors will be corrected;</li>
                  <li>The results obtained from use of the Service will be accurate or reliable;</li>
                  <li>The Service is free from viruses, malware, or other harmful components;</li>
                  <li>Any data transmitted to or from the Service will be secure or not intercepted; or</li>
                  <li>Third-party integrations or linked services will function correctly.</li>
                </ul>

                <h3>13.3 Third-Party Content</h3>
                <p>
                  The Service may contain links to third-party websites or content. We are not
                  responsible for and do not endorse any third-party sites, content, products,
                  or services. Your use of third-party resources is at your own risk.
                </p>

                <h3>13.4 Corrections</h3>
                <p>
                  There may be information on the Service that contains typographical errors,
                  inaccuracies, or omissions, including descriptions, game rules, rankings,
                  statistics, and various other information. We reserve the right to:
                </p>
                <ul>
                  <li>Correct any errors, inaccuracies, or omissions;</li>
                  <li>Change or update information on the Service at any time without prior notice;</li>
                  <li>Modify game rules, piece behaviors, or other gameplay elements; and</li>
                  <li>Adjust rankings or statistics to correct errors or address exploitation.</li>
                </ul>
                <p>
                  We are under no obligation to update any information on the Service, and
                  you should not rely on the completeness or accuracy of such information
                  for any purpose.
                </p>

                <h3>13.5 Security Disclaimer</h3>
                <p>
                  While we implement reasonable security measures, we do not warrant or
                  guarantee the security of our servers, networks, or any data transmitted
                  to or stored by the Service. You acknowledge that:
                </p>
                <ul>
                  <li>
                    No method of electronic transmission or storage is completely secure;
                  </li>
                  <li>
                    We cannot guarantee that unauthorized third parties will never be able
                    to defeat our security measures or improperly access your data;
                  </li>
                  <li>
                    You provide personal information and data at your own risk; and
                  </li>
                  <li>
                    We are not responsible for circumvention of any privacy settings or
                    security measures contained on the Service.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 14: Limitation of Liability */}
            <section id="liability" className="terms-section">
              <h2>14. Limitation of Liability</h2>

              <div className="legal-box">
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
                  Expanded Chess, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS
                  BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                  DAMAGES, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul>
                  <li>LOSS OF PROFITS, REVENUE, OR DATA;</li>
                  <li>LOSS OF GOODWILL;</li>
                  <li>SERVICE INTERRUPTION;</li>
                  <li>COMPUTER DAMAGE; OR</li>
                  <li>OTHER INTANGIBLE LOSSES,</li>
                </ul>
                <p>ARISING OUT OF OR IN CONNECTION WITH:</p>
                <ul>
                  <li>YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR USE, THE SERVICE;</li>
                  <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE;</li>
                  <li>ANY USER CONTENT; OR</li>
                  <li>UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT.</li>
                </ul>

                <h3>Liability Cap</h3>
                <p>
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF
                  OR RELATING TO THE SERVICE EXCEED ZERO UNITED STATES DOLLARS (US $0).
                </p>

                <p>
                  Some jurisdictions do not allow the exclusion or limitation of liability for
                  certain damages; in such jurisdictions, our liability shall be limited to the
                  maximum extent permitted by law.
                </p>
              </div>
            </section>

            {/* Section 15: Indemnification */}
            <section id="indemnification" className="terms-section">
              <h2>15. Indemnification</h2>
              <p>
                You agree to defend, indemnify, and hold harmless Expanded Chess, its officers,
                directors, employees, agents, and licensors from and against any claims, damages,
                obligations, losses, liabilities, costs, or debt, and expenses (including reasonable
                attorneys' fees) arising from:
              </p>
              <ul>
                <li>Your use of or access to the Service;</li>
                <li>Your violation of any provision of these Terms;</li>
                <li>Your violation of any third party's rights, including intellectual property or privacy rights; or</li>
                <li>Any claim that your User Content caused damage to a third party.</li>
              </ul>
              <p>
                We reserve the right, at your expense, to assume the exclusive defense and control
                of any matter subject to indemnification by you, and you agree to cooperate in such defense.
              </p>
            </section>

            {/* Section 16: Dispute Resolution */}
            <section id="disputes" className="terms-section">
              <h2>16. Dispute Resolution</h2>

              <h3>16.1 Informal Resolution</h3>
              <p>
                Before filing any legal claim, you shall contact us at info@expandedchess.com to
                attempt to resolve the dispute informally. We will attempt to resolve disputes
                by communicating with you via email. If a dispute is not resolved within thirty
                (30) days of your initial submission, either party may proceed with formal legal action.
              </p>

              <h3>16.2 Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of
                the State of Oregon, United States, without regard to its conflict-of-law provisions.
              </p>

              <h3>16.3 Jurisdiction</h3>
              <p>
                Any legal action or proceeding arising out of or relating to these Terms or the
                Service shall be brought exclusively in the courts located in the State of Oregon, United States,
                and you consent to the personal jurisdiction and venue of such courts.
              </p>

              <h3>16.4 Class Action Waiver</h3>
              <p>
                YOU AGREE THAT ANY CLAIMS SHALL BE BROUGHT IN YOUR INDIVIDUAL CAPACITY AND
                NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, OR
                REPRESENTATIVE PROCEEDING.
              </p>

              <h3>16.5 Time Limitation</h3>
              <p>
                To the maximum extent permitted by law, any claim arising out of or relating
                to these Terms or the Service must be brought within one (1) year after the
                cause of action accrues. Otherwise, such claim is permanently barred.
              </p>
            </section>

            {/* Section 17: Changes to Terms */}
            <section id="changes" className="terms-section">
              <h2>17. Changes to Terms</h2>

              <h3>17.1 Modifications</h3>
              <p>
                We reserve the right to modify these Terms at any time. When we make material
                changes, we will:
              </p>
              <ul>
                <li>Update the "Effective Date" at the top of these Terms; and</li>
                <li>Provide notice via the Service and/or email where appropriate.</li>
              </ul>

              <h3>17.2 Acceptance of Changes</h3>
              <p>
                Your continued use of the Service following the effective date of any changes
                constitutes your acceptance of the modified Terms. If you do not agree to the
                modified Terms, you must stop using the Service.
              </p>
            </section>

            {/* Section 18: Electronic Communications */}
            <section id="electronic" className="terms-section">
              <h2>18. Electronic Communications, Transactions, and Signatures</h2>

              <h3>18.1 Consent to Electronic Communications</h3>
              <p>
                By using the Service, you consent to receive electronic communications from us,
                including but not limited to emails, in-app notifications, and notices posted
                on the Service. You agree that all agreements, notices, disclosures, and other
                communications we provide to you electronically satisfy any legal requirement
                that such communications be in writing.
              </p>

              <h3>18.2 Electronic Agreements</h3>
              <p>
                You acknowledge and agree that clicking "I Agree," "Accept," "Submit," or
                similar buttons, or using the Service after being presented with these Terms,
                constitutes your electronic signature and acceptance of these Terms with the
                same legal force and effect as a handwritten signature.
              </p>

              <h3>18.3 Electronic Records</h3>
              <p>
                You hereby agree to the use of electronic signatures, contracts, orders, and
                other records, and to electronic delivery of notices, policies, and records
                of transactions initiated or completed through the Service. You waive any
                rights or requirements under any laws in any jurisdiction that require an
                original signature, delivery or retention of non-electronic records, or to
                payments or credits by means other than electronic means.
              </p>

              <h3>18.4 Communication Preferences</h3>
              <p>
                You may update your communication preferences through your account settings
                or by contacting us at <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>.
                Note that certain communications, such as service announcements and
                administrative messages, are necessary for the operation of your account
                and cannot be opted out of while maintaining an active account.
              </p>
            </section>

            {/* Section 19: General Provisions */}
            <section id="general" className="terms-section">
              <h2>19. General Provisions</h2>

              <h3>19.1 Entire Agreement</h3>
              <p>
                These Terms, together with our Privacy Policy and any other legal notices posted
                on the Service, constitute the entire agreement between you and Expanded Chess
                regarding the Service and supersede all prior agreements.
              </p>

              <h3>19.2 Severability</h3>
              <p>
                If any provision of these Terms is held invalid or unenforceable, that provision
                will be enforced to the maximum extent permissible and the remaining provisions
                will remain in full force and effect.
              </p>

              <h3>19.3 Waiver</h3>
              <p>
                No waiver of any term of these Terms shall be deemed a further or continuing
                waiver of such term or any other term. Our failure to assert any right or
                provision under these Terms shall not constitute a waiver of such right or provision.
              </p>

              <h3>19.4 Assignment</h3>
              <p>
                You may not assign or transfer these Terms, by operation of law or otherwise,
                without our prior written consent. We may freely assign or transfer these Terms
                without restriction.
              </p>

              <h3>19.5 Notices</h3>
              <p>
                We may send notices to you via email to the address associated with your account
                or by posting notices on the Service. You shall direct legal notices to the
                contact details set out below.
              </p>

              <h3>19.6 Force Majeure</h3>
              <p>
                We shall not be liable for any failure to perform due to causes beyond our
                reasonable control, including without limitation: acts of God, war, terrorism,
                riots, embargoes, acts of civil or military authorities, fire, flood, natural
                disasters, strikes, or shortages of transportation, facilities, fuel, energy,
                labor, or materials.
              </p>

              <h3>19.7 No Third-Party Beneficiaries</h3>
              <p>
                These Terms do not create any third-party beneficiary rights in any individual
                or entity not a party to these Terms.
              </p>
            </section>

            {/* Section 20: Contact Information */}
            <section id="contact" className="terms-section">
              <h2>20. Contact Information</h2>
              <p>
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="contact-box">
                <p><strong>Expanded Chess</strong></p>
                <p>Attn: Legal Department</p>
                <p>Box 125, 1000 S Hwy 395, Hermiston, OR 97838</p>
                <p>United States</p>
                <p>Email: <a href="mailto:info@expandedchess.com">info@expandedchess.com</a></p>
              </div>
            </section>

            {/* Print Button - Bottom */}
            <div className="terms-footer">
              <button
                className="print-btn"
                onClick={() => window.print()}
              >
                🖨️ Print / Save as PDF
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TermsOfService;
